import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, LogOut, Menu, Volume2, VolumeX, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSite } from "@/context/SiteContext";

const MAIN = [["/story", "Our Story"], ["/tree", "Family Tree"], ["/enterprise", "Our Enterprise"], ["/gallery", "Gallery"]];
const REMEMBER = [["/memoriam", "In Memoriam"], ["/voices", "Family Voices"], ["/places", "Where We've Lived"]];

export const Nav = () => {
  const { session, logout, sound, setSound } = useSite() || {};
  const [open, setOpen] = useState(false);
  const [rem, setRem] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); setRem(false); }, [loc.pathname]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  const link = "sweep text-sm tracking-wide text-[#F6EFE0]/85 hover:text-[#F6EFE0] transition-colors";
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${scrolled ? "bg-[#0B1D14]/80 backdrop-blur-xl border-b border-[#C9A24B]/20" : "border-b border-transparent"}`} data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <Logo size={34} className="transition-transform duration-700 group-hover:rotate-[20deg]" />
          <span className="font-display tracking-[0.22em] text-xs sm:text-sm text-[#F6EFE0] whitespace-nowrap">MARAIS<span className="text-[#C9A24B]"> FAMILY</span></span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {MAIN.map(([to, l]) => <NavLink key={to} to={to} className={link} data-testid={`nav-${to.slice(1)}`}>{l}</NavLink>)}
          <div className="relative" onMouseEnter={() => setRem(true)} onMouseLeave={() => setRem(false)}>
            <button className={`${link} inline-flex items-center gap-1`} onClick={() => setRem((v) => !v)} data-testid="nav-remember">Remember <ChevronDown size={14} /></button>
            <AnimatePresence>
              {rem && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className="absolute right-0 top-full pt-3">
                  <div className="glass rounded-xl p-2 w-56">
                    {REMEMBER.map(([to, l]) => <NavLink key={to} to={to} className="block px-4 py-2.5 rounded-lg text-sm text-[#F6EFE0]/90 hover:bg-[#C9A24B]/15" data-testid={`nav-${to.slice(1)}`}>{l}</NavLink>)}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => setSound(!sound)} className="lb-btn !w-9 !h-9" aria-label="Toggle sound" data-testid="sound-toggle">{sound ? <Volume2 size={15} /> : <VolumeX size={15} />}</button>
          {session?.active ? (
            <>
              {session.scope === "family" && <span className="hidden sm:block"><Link to="/family" className="btn-ghost btn-sm" data-testid="nav-family-area">Family area</Link></span>}
              {session.scope === "personal" && session.target && <span className="hidden sm:block"><Link to={`/personal/${session.target}`} className="btn-ghost btn-sm" data-testid="nav-my-page">My page</Link></span>}
              <button onClick={logout} className="lb-btn !w-9 !h-9" aria-label="Log out" data-testid="nav-logout"><LogOut size={15} /></button>
            </>
          ) : (
            <span className="hidden sm:block"><Link to="/gate" className="btn-ghost btn-sm" data-testid="nav-members-only">Family Members Only</Link></span>
          )}
          <span className="lg:hidden"><button className="lb-btn !w-9 !h-9" onClick={() => setOpen(true)} aria-label="Menu" data-testid="nav-mobile-open"><Menu size={16} /></button></span>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[70] bg-[#0B1D14] flex flex-col p-6" initial={{ clipPath: "circle(0% at 95% 4%)" }} animate={{ clipPath: "circle(150% at 95% 4%)" }} exit={{ clipPath: "circle(0% at 95% 4%)" }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} data-testid="mobile-menu">
            <div className="flex justify-between items-center"><Logo /><button className="lb-btn" onClick={() => setOpen(false)} data-testid="nav-mobile-close"><X size={18} /></button></div>
            <div className="mt-10 flex flex-col gap-5">
              {[...MAIN, ...REMEMBER, ["/gate", "Family Members Only"]].map(([to, l], i) => (
                <motion.div key={to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.05 }}>
                  <NavLink to={to} className="font-display text-3xl text-[#F6EFE0]" data-testid={`mnav-${to.slice(1)}`}>{l}</NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
